import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PropTypes from 'prop-types';
import SecurityCodeInputField from './SecurityCodeInputField';
import JunoButton from '../../common/JunoButton';
import JunoModal from '../../common/JunoModal';
import CopyButton from '../../common/CopyButton';
import { format } from 'date-fns'; 

const VerificationModal = ({
    open,
    onClose,
    isActivated,
    qrCodeData,
    changeActivation,
    error,
    inputValue,
    setInputValue,
    setError,
    secret,
    isLoading,
    password,
    setPassword,
    handlePasswordCheck,
    verifyPasswordModalOpen

}) => {
    const [step, setStep] = useState(1);
    const [progress, setProgress] = useState(50);
    const [showPassword, setShowPassword] = useState(false);
    const togglePasswordVisibility = () => setShowPassword(!showPassword);

    const clientData = useSelector(state => state.juno.clientData);


    useEffect(() => {
        if (open) {
            if (isActivated) {
                setStep(2);
                setProgress(100);
            } else {
                setStep(1);
                setProgress(50);
            }
        }
    }, [open, isActivated]);

    const handleNextClick = () => {
        if (step === 1) {
            setStep(2);
            setProgress(100);
        } else {
            changeActivation();
        }
    };

    const getButtonText = () => {
        if (step === 1) return 'Next';
        return isActivated ? 'Disable 2FA' : 'Finish';
    };

    const handleInputChange = value => {
        setInputValue(value);
        if (error) setError('');
    };

      // Handle password input change
      const handlePasswordChange = (e) => {
        const newValue = e.target.value;
        setPassword(newValue);
        
        if (!newValue.trim()) {
            setError('');
        }
    };

      const twoStepStatus = () => (
        <span
            className={`inline-block mt-1 px-2 py-1 text-xs font-semibold rounded-sm
                ${
                    !isActivated
                        ? 'bg-[#B62B2B29] text-[#5F0000] dark:bg-[#F970661F] dark:text-[#F97066]'
                        : 'bg-[#82B36029] text-[#275F00] dark:bg-[#3CCB7F1F] dark:text-[#3CCB7F]'
                }
            `}>
            {isActivated ? 'Enabled' : 'Disabled'}
        </span>
    );

    const getModalTitle = () => {
        if (verifyPasswordModalOpen) {
            return (
                <div className="flex justify-between items-center gap-[2px] sm:gap-3">
                    <span className="text-lg font-semibold uppercase">Two-Step Verification</span>
                    {twoStepStatus()}
                </div>
            );
        } else {
            return `${isActivated ? 'Disable' : 'Enable'} Two-Step Verification`;
        }
    };

    const activeStepStyle = "!text-text text-sm font-semibold uppercase";
    const inactiveStepStyle = "!text-gray-custom text-sm font-medium uppercase ";

    return (
        <JunoModal
            open={open}
            onClose={onClose}
            showSlide={true}
            title={getModalTitle()}
            fullScreenMobile={true}
            content={() =>
                verifyPasswordModalOpen ? (
                    <div className="flex flex-col h-full">
                        <div className="flex-grow">
                            {!isActivated ? (
                                <div className="!mt-3 !mb-3">
                                    <p className="text-balanceCard-manage text-sm font-medium leading-5 uppercase">
                                    Protect your account by adding an extra layer of security. 
                                    A second login step can keep your account secure, even if your password is compromised. 
                                    To enable it, all you need is a smart phone.
                                    </p>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-4 !mt-3 !mb-3">
                                    <p className="text-balanceCard-manage text-sm font-medium leading-5 uppercase">
                                        You've kept your account extra secure with a second login step since:
                                    </p>
                                    <p className="self-stretch rounded-md !text-primary text-base font-semibold">
                                        {format(new Date(clientData?.twoFA?.enabledDate), 'MMM d, yyyy, h:mm a')}
                                    </p>
                                    <p className="text-balanceCard-manage text-sm font-medium leading-5 uppercase">
                                    For extra security, enter your password to disable two-step verification. 
                                    In that case your account will no longer have the protection of a second login step.
                                    </p>
                                </div>
                            )}

                            <div className="w-full mt-6">
                                <label
                                    htmlFor="password"
                                    className="block text-xs font-medium text-balanceCard-manage mb-2 uppercase leading-none text-left w-full"
                                >
                                    Password
                                </label>
                                <div className="relative">
                                    <input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="ENTER JUNO MONEY PASSWORD"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        className="custom-input w-full bg-card-bg !text-text rounded-md text-sm font-medium py-2 px-3 pr-10 h-12 leading-tight hover:bg-transparent hover:border hover:border-[#D1D1D6]"
                                        autoComplete="new-password"
                                    />
                                    <div
                                        className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer"
                                        onClick={togglePasswordVisibility}
                                    >
                                        {showPassword ? (
                                            <Visibility fontSize="medium" className="text-balanceCard-manage" />
                                        ) : (
                                            <VisibilityOff fontSize="medium" className="text-balanceCard-manage" />
                                        )}
                                    </div>
                                </div>
                                {error && <div className="text-red-500 text-sm mt-1">{error}</div>}
                            </div>
                        </div>

                        <div className="mt-auto sm:mt-12 sm:pt-0 sticky bottom-0 left-0 right-0">
                            <JunoButton
                                onClick={handlePasswordCheck}
                                loading={isLoading}
                                className="grow-1 !font-semibold !text-sm !leading-tight w-full mt-12 md:!mb-0 mb-11"
                                disabled={!password}
                            >
                                {!isActivated ? 'Set up' : 'Disable 2FA'}
                            </JunoButton>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col h-full">
                        <div className="flex-grow overflow-y-auto">
                        {/* Progress Bar */}
                        <Box className="!mb-9 !mt-6">
                            <div
                                className="relative h-3 overflow-hidden"
                                style={{
                                    backgroundColor: 'var(--expand-graph-border)',
                                }}>
                                <div
                                    className="absolute left-0 top-0 h-full transition-all duration-500 ease-in-out"
                                    style={{
                                        width: `${progress}%`,
                                        backgroundColor: 'var(--color-primary)',
                                    }}></div>
                            </div>

                            <div className="flex justify-around mt-2 px-1">
                                <span className={step === 1 ? activeStepStyle : inactiveStepStyle}>
                                    Scan QR
                                </span>
                                <span className={step === 2 ? activeStepStyle : inactiveStepStyle}>
                                    Enter Code
                                </span>
                            </div>
                        </Box>
                        
                             {/* Step 1: QR Code Scanning (only shown for enabling 2FA) */}
                        {step === 1 && !isActivated ? (
                            <Box className="flex flex-col">
                                <div className="flex flex-col gap-3">
                                    <p className="self-stretch font-semibold rounded-md !text-primary text-base uppercase">
                                        Scan or copy the seed with authenticator app.
                                    </p>
                                    <p className="text-balanceCard-manage text-sm font-medium leading-5 uppercase">
                                        You'll need to use a verification app such as Google
                                        Authenticator, Authy, Duo or similar. Install from your app
                                        store
                                    </p>
                                </div>

                                {/* QR Code Display */}
                                <div className="flex justify-center my-6">
                                    {qrCodeData && <img src={qrCodeData} alt="QR Code for 2FA" />}
                                </div>

                                {/* Secret Key */}
                                <div className="flex flex-col gap-2">
                                    <p className="text-gray-custom text-xs !font-semibold leading-4 uppercase">
                                        Authentication setup key
                                    </p>
                                    <div className="flex justify-between items-center gap-2 h-[52px] rounded-lg bg-card-bg text-text px-3 pr-4 pl-4">
                                        <p className="truncate max-w-[calc(100%-40px)] lowercase">
                                            {secret?.base32}
                                        </p>
                                        <CopyButton
                                            className="text-[#1A8A7E]"
                                            copyValue={secret?.base32}
                                        />
                                    </div>
                                </div>
                            </Box>
                        ) : (
                            <Box className="flex flex-col">
                                <div className="flex flex-col gap-3">
                                    <p className="self-stretch font-semibold rounded-md !text-primary text-base uppercase">
                                        Enter generated 6-digit code.
                                    </p>
                                    <p className="text-balanceCard-manage text-sm font-medium leading-5 uppercase">
                                        {isActivated
                                            ? 'Enter the code from your authenticator app to disable 2FA.'
                                            : "Enter the code from the app below. Once connected, we'll remember your phone so you can use it each time you log in."}
                                    </p>
                                </div>

                                {/* Authentication Code Input */}
                                <div className="mt-8">
                                    <SecurityCodeInputField
                                        onChange={handleInputChange}
                                        value={inputValue}
                                    />
                                </div>

                                {/* Error Message */}
                                {error && <div className="mt-1 text-red-500">{error}</div>}
                            </Box>
                        )}
                    </div>

                        <div className="mt-auto sm:mt-12 sm:pb-0 sm:pt-0 sticky sm:static bottom-0 left-0 right-0">
                            <JunoButton
                                onClick={handleNextClick}
                                loading={isLoading}
                                className="!bg-buttons-tx-modal-bg font-semibold leading-5 w-full md:!mb-0 mb-11 mt-12">
                                {getButtonText()}
                            </JunoButton>
                        </div>
                    </div>
                )
            }
        />
    );
};

VerificationModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    isActivated: PropTypes.bool.isRequired,
    qrCodeData: PropTypes.string,
    changeActivation: PropTypes.func.isRequired,
    error: PropTypes.string,
    inputValue: PropTypes.string.isRequired,
    setInputValue: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    secret: PropTypes.shape({
        base32: PropTypes.string,
    }),
    isLoading: PropTypes.bool,
    twoStepModalOpen: PropTypes.bool,
    clientData: PropTypes.object,
};

export default VerificationModal;
